import { createRouter, createWebHistory } from 'vue-router';
import { getPublicConfig, getModuleAccessRules, getPluginAccessRules } from '@/api/config';
import { useUserStore } from '@/store/modules/user';
import { setAccessRules } from '@/utils/accessControl';

let siteName = '';
let sessionRestored = false;
let moduleAccessRules = null;
let moduleAccessRulesLoading = false;
let pluginAccessRules = null;
let pluginAccessRulesLoading = false;

const updateFavicon = (iconUrl) => {
  if (!iconUrl) return;
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = iconUrl + '?t=' + Date.now();
};

const initSiteConfig = async () => {
  try {
    const response = await getPublicConfig();
    if (response.code === 200 && response.data.config) {
      siteName = response.data.config.site_name || '';
      
      if (response.data.config.site_icon) {
        const iconUrl = response.data.config.site_icon.startsWith('http') 
          ? response.data.config.site_icon 
          : window.location.origin + response.data.config.site_icon;
        updateFavicon(iconUrl);
      }
    }
  } catch (error) {
    console.error('获取网站配置失败:', error);
  }
};

const initModuleAccessRules = async (forceRefresh = false) => {
  console.log('[ModuleAccess] initModuleAccessRules called, forceRefresh:', forceRefresh, 'current rules:', moduleAccessRules);
  
  if (moduleAccessRules !== null && !forceRefresh) {
    console.log('[ModuleAccess] Returning cached rules');
    return moduleAccessRules;
  }
  
  if (moduleAccessRulesLoading) {
    console.log('[ModuleAccess] Waiting for loading to complete...');
    await new Promise(resolve => setTimeout(resolve, 100));
    return moduleAccessRules;
  }
  
  moduleAccessRulesLoading = true;
  console.log('[ModuleAccess] Starting API request...');
  
  try {
    const response = await getModuleAccessRules();
    console.log('[ModuleAccess] API Response:', response);
    if (response.code === 200 && response.data) {
      moduleAccessRules = response.data;
      setAccessRules(moduleAccessRules);
      console.log('[ModuleAccess] Rules loaded:', moduleAccessRules);
      return moduleAccessRules;
    }
  } catch (error) {
    console.error('[ModuleAccess] 获取模块访问规则失败:', error);
  } finally {
    moduleAccessRulesLoading = false;
  }
  
  console.log('[ModuleAccess] Using default empty rules');
  return { rules: [], default_require_login: false, default_min_role_level: 0 };
};

const initPluginAccessRules = async (forceRefresh = false) => {
  console.log('[PluginAccess] initPluginAccessRules called, forceRefresh:', forceRefresh, 'current rules:', pluginAccessRules);
  
  if (pluginAccessRules !== null && !forceRefresh) {
    console.log('[PluginAccess] Returning cached rules');
    return pluginAccessRules;
  }
  
  if (pluginAccessRulesLoading) {
    console.log('[PluginAccess] Waiting for loading to complete...');
    await new Promise(resolve => setTimeout(resolve, 100));
    return pluginAccessRules;
  }
  
  pluginAccessRulesLoading = true;
  console.log('[PluginAccess] Starting API request...');
  
  try {
    const response = await getPluginAccessRules();
    console.log('[PluginAccess] API Response:', response);
    if (response.code === 200 && response.data) {
      pluginAccessRules = response.data;
      console.log('[PluginAccess] Rules loaded:', pluginAccessRules);
      return pluginAccessRules;
    }
  } catch (error) {
    console.error('[PluginAccess] 获取插件访问规则失败:', error);
  } finally {
    pluginAccessRulesLoading = false;
  }
  
  console.log('[PluginAccess] Using default empty rules');
  return { rules: [], default_require_login: false, default_min_role_level: 0 };
};

const restoreSession = async () => {
  if (sessionRestored) return;
  
  if (localStorage.getItem('isLoggedIn')) {
    const userStore = useUserStore();
    const success = await userStore.restoreSession();
    if (!success) {
      sessionRestored = false;
      return;
    }
  }
  sessionRestored = true;
};

initSiteConfig();

const getModuleFromPath = (path) => {
  const pathParts = path.split('/').filter(Boolean);
  if (pathParts.length === 0) return 'home';
  
  const moduleMap = {
    'blog': 'blog',
    'info': 'info',
    'shuji': 'shuji',
    'nav': 'nav',
    'user': 'user',
    'page': 'page'
  };
  
  return moduleMap[pathParts[0]] || pathParts[0];
};

const getRelativePath = (fullPath, module) => {
  if (module === 'home') return '/';
  
  const modulePrefix = '/' + module;
  if (fullPath === modulePrefix) return '/';
  
  if (fullPath.startsWith(modulePrefix + '/')) {
    return fullPath.substring(modulePrefix.length);
  }
  
  return fullPath;
};

const checkPathMatch = (pattern, path) => {
  if (pattern === path) return true;
  
  if (pattern === '/*') return true;
  
  if (pattern.endsWith('/*')) {
    const basePath = pattern.slice(0, -2);
    return path.startsWith(basePath);
  }
  
  const regexPattern = pattern
    .replace(/:[a-zA-Z_]+/g, '[^/]+')
    .replace(/\*/g, '.*');
  const regex = new RegExp('^' + regexPattern + '$');
  return regex.test(path);
};

const getAccessRule = (module, fullPath) => {
  if (!moduleAccessRules || !moduleAccessRules.rules) {
    console.log('[ModuleAccess] No rules loaded, using defaults');
    return { require_login: false, min_role_level: 0 };
  }
  
  const relativePath = getRelativePath(fullPath, module);
  console.log('[ModuleAccess] Checking access for:', { module, fullPath, relativePath, rulesCount: moduleAccessRules.rules.length });
  
  for (const rule of moduleAccessRules.rules) {
    if (rule.module === module && checkPathMatch(rule.path, relativePath)) {
      console.log('[ModuleAccess] Matched rule:', rule);
      return {
        require_login: rule.require_login,
        min_role_level: rule.min_role_level
      };
    }
  }
  
  console.log('[ModuleAccess] No matching rule found, using defaults');
  return {
    require_login: moduleAccessRules.default_require_login || false,
    min_role_level: moduleAccessRules.default_min_role_level || 0
  };
};

const getPluginAccessRule = (plugin, fullPath) => {
  if (!pluginAccessRules || !pluginAccessRules.rules) {
    console.log('[PluginAccess] No rules loaded, using defaults');
    return { require_login: false, min_role_level: 0 };
  }
  
  const relativePath = getRelativePath(fullPath, plugin);
  console.log('[PluginAccess] Checking access for:', { plugin, fullPath, relativePath, rulesCount: pluginAccessRules.rules.length });
  
  for (const rule of pluginAccessRules.rules) {
    if (rule.plugin === plugin && checkPathMatch(rule.path, relativePath)) {
      console.log('[PluginAccess] Matched rule:', rule);
      return {
        require_login: rule.require_login,
        min_role_level: rule.min_role_level
      };
    }
  }
  
  console.log('[PluginAccess] No matching rule found, using defaults');
  return {
    require_login: pluginAccessRules.default_require_login || false,
    min_role_level: pluginAccessRules.default_min_role_level || 0
  };
};

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { title: '首页' }
  },
  
  // 全局搜索路由
  {
    path: '/search',
    name: 'Search',
    component: () => import('@/views/Search.vue'),
    meta: { title: '搜索' }
  },
  
  // 博客路由
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('@/views/blog/Index.vue'),
    meta: { title: '博客' }
  },
  {
    path: '/blog/:id',
    name: 'BlogDetail',
    component: () => import('@/views/blog/Detail.vue'),
    meta: { title: '文章详情' }
  },
  {
    path: '/blog/create',
    name: 'BlogCreate',
    component: () => import('@/views/blog/Create.vue'),
    meta: { title: '创建文章', requiresAuth: true }
  },
  {
    path: '/blog/edit/:id',
    name: 'BlogEdit',
    component: () => import('@/views/blog/Edit.vue'),
    meta: { title: '编辑文章', requiresAuth: true }
  },
  {
    path: '/blog/category/:id',
    name: 'BlogCategory',
    component: () => import('@/views/blog/Category.vue'),
    meta: { title: '分类' }
  },
  {
    path: '/blog/tag/:id',
    name: 'BlogTag',
    component: () => import('@/views/blog/Tag.vue'),
    meta: { title: '标签' }
  },
  {
    path: '/blog/search',
    name: 'BlogSearch',
    component: () => import('@/views/blog/Search.vue'),
    meta: { title: '搜索' }
  },
  
  // 信息路由
  {
    path: '/info',
    name: 'Info',
    component: () => import('@/views/info/Index.vue'),
    meta: { title: '信息' }
  },
  {
    path: '/info/category/:id',
    name: 'InfoCategory',
    component: () => import('@/views/info/Category.vue'),
    meta: { title: '分类' }
  },
  {
    path: '/info/create',
    name: 'InfoCreate',
    component: () => import('@/views/info/Create.vue'),
    meta: { title: '创建信息', requiresAuth: true }
  },
  {
    path: '/info/edit/:id',
    name: 'InfoEdit',
    component: () => import('@/views/info/Edit.vue'),
    meta: { title: '编辑信息', requiresAuth: true }
  },
  {
    path: '/info/:id',
    name: 'InfoDetail',
    component: () => import('@/views/info/Detail.vue'),
    meta: { title: '信息详情' }
  },
  
  // 书籍路由
  {
    path: '/shuji',
    name: 'Shuji',
    component: () => import('@/views/shuji/Index.vue'),
    meta: { title: '书籍' }
  },
  {
    path: '/shuji/:id',
    name: 'ShujiDetail',
    component: () => import('@/views/shuji/Detail.vue'),
    meta: { title: '书籍详情' }
  },
  {
    path: '/shuji/create',
    name: 'ShujiCreate',
    component: () => import('@/views/shuji/Create.vue'),
    meta: { title: '创建书籍', requiresAuth: true }
  },
  {
    path: '/shuji/edit/:id',
    name: 'ShujiEdit',
    component: () => import('@/views/shuji/Edit.vue'),
    meta: { title: '编辑书籍', requiresAuth: true }
  },
  {
    path: '/shuji/category/:id',
    name: 'ShujiCategory',
    component: () => import('@/views/shuji/Category.vue'),
    meta: { title: '书籍分类' }
  },
  
  // 导航路由
  {
    path: '/nav',
    name: 'Nav',
    component: () => import('@/views/nav/Index.vue'),
    meta: { title: '导航' }
  },
  {
    path: '/nav/category/:id',
    name: 'NavCategory',
    component: () => import('@/views/nav/Category.vue'),
    meta: { title: '分类' }
  },
  {
    path: '/nav/:id',
    name: 'NavDetail',
    component: () => import('@/views/nav/Detail.vue'),
    meta: { title: '站点详情' }
  },
  {
    path: '/nav/submit',
    name: 'NavSubmit',
    component: () => import('@/views/nav/Submit.vue'),
    meta: { title: '提交站点', requiresAuth: true }
  },
  
  // 用户路由
  {
    path: '/user/login',
    name: 'Login',
    component: () => import('@/views/user/Login.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/user/register',
    name: 'Register',
    component: () => import('@/views/user/Register.vue'),
    meta: { title: '注册' }
  },
  {
    path: '/user/profile',
    name: 'Profile',
    component: () => import('@/views/user/Profile.vue'),
    meta: { title: '个人资料', requiresAuth: true }
  },
  {
    path: '/user/edit-profile',
    name: 'EditProfile',
    component: () => import('@/views/user/EditProfile.vue'),
    meta: { title: '编辑资料', requiresAuth: true }
  },
  {
    path: '/user/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/user/ForgotPassword.vue'),
    meta: { title: '忘记密码' }
  },
  
  // 页面路由
  {
    path: '/page/:slug',
    name: 'Page',
    component: () => import('@/views/Page.vue'),
    meta: { title: '页面' }
  },
  
  // 分类别名路由（在 404 路由之前）
  {
    path: '/:slug',
    name: 'BlogCategoryBySlug',
    component: () => import('@/views/blog/Category.vue'),
    meta: { title: '分类' },
    props: route => ({ slug: route.params.slug }),
    beforeEnter: (to) => {
      const reservedPaths = ['blog', 'info', 'shuji', 'nav', 'user', 'page'];
      if (reservedPaths.includes(to.params.slug)) {
        return false;
      }
    }
  },
  
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: '页面不存在' }
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  console.log('[Router] Navigation to:', to.path);
  
  const appTitle = siteName || import.meta.env.VITE_APP_TITLE;
  document.title = to.meta.title ? `${to.meta.title} - ${appTitle}` : appTitle;
  
  console.log('[Router] Calling initModuleAccessRules...');
  await initModuleAccessRules();
  console.log('[Router] initModuleAccessRules completed');
  
  console.log('[Router] Calling initPluginAccessRules...');
  await initPluginAccessRules();
  console.log('[Router] initPluginAccessRules completed');
  
  const module = getModuleFromPath(to.path);
  const accessRule = getAccessRule(module, to.path);
  const pluginAccessRule = getPluginAccessRule(module, to.path);
  
  console.log('[Router] Access check:', { module, accessRule, pluginAccessRule });
  
  const needsAuth = to.meta.requiresAuth || accessRule.require_login || pluginAccessRule.require_login;
  const minRoleLevel = Math.max(accessRule.min_role_level || 0, pluginAccessRule.min_role_level || 0);
  
  const userStore = useUserStore();
  const isLoggedIn = localStorage.getItem('isLoggedIn');
  
  if (isLoggedIn && !userStore.isLoggedIn) {
    await restoreSession();
  }
  
  if (needsAuth) {
    if (!userStore.isLoggedIn) {
      console.log('[Router] User not logged in, redirecting to login');
      next({
        path: '/user/login',
        query: { redirect: to.fullPath }
      });
      return;
    }
    
    if (minRoleLevel > 0) {
      const userRoleLevel = userStore.user?.role_level || 0;
      if (userRoleLevel < minRoleLevel) {
        console.log('[Router] User role level insufficient:', userRoleLevel, '<', minRoleLevel);
        next({
          path: '/',
          query: { error: 'permission_denied' }
        });
        return;
      }
    }
  }
  
  next();
});

router.afterEach((to, from) => {
  // 页面加载完成后的处理
  console.log('Navigation completed:', to.path);
});

export const getRouterAccessRules = () => {
  console.log('[Router] getRouterAccessRules called, returning:', moduleAccessRules);
  return moduleAccessRules;
};

export default router;
